import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import LoadingIcon from 'components/atoms/LoadingIcon'
import { Formik, Form, Field, FieldArray } from 'formik'
import * as Yup from 'yup'
import { CameraIcon } from '@heroicons/react/solid'
import { CheckIcon, PlusCircleIcon, TrashIcon, PhoneIcon, ChatAlt2Icon } from '@heroicons/react/outline'
import { size, get, xor, isArray, filter, reject, some, find, forEach, includes, isNil, isString, isObject, trim, toLower } from 'lodash'
import Geocode from 'react-geocode'

import Input from 'components/atoms/Input'
import InputPhone from 'components/atoms/PhoneInput'
import notify from 'components/atoms/Notify'
import Selection from 'components/molecules/Selection'
import apiPost from 'lib/network/apiPost'
import apiPut from 'lib/network/apiPut'
import apiDelete from 'lib/network/apiDelete'
import TextArea from 'antd/lib/input/TextArea'
import classNames from 'classnames'
import Switch from 'components/atoms/Switch'
import apiGet from 'lib/network/apiGet'
import BranchTimingDialog from './branchTimingDialog/hoursDialog'
import ShiftModeDialog from './branchTimingDialog/shiftDialog'
import NormalModeDialog from './branchTimingDialog/normalDialoh'
import timezonesData from './timeZone'
import { DashboardCards } from './carStats'
import useModal from 'hooks/useModal'
import LocationPicker from 'pages/booking/modals/googleMaps'

// Configure Geocode
Geocode.setApiKey(process.env.REACT_APP_GOOGLE_MAPS_API_KEY)
Geocode.setLanguage('en')
Geocode.setLocationType('ROOFTOP')

const STEP_ONE_FIELDS = ['name', 'company', 'country', 'governorate', 'lat', 'lon', 'email', 'address', 'timeZone']
const REQUIRED_FIELDS = new Set([...STEP_ONE_FIELDS, 'phones.title', 'phones.phone', 'whatsappConfirmationAlert'])

const LabelContent = ({ label, required }) => (
  <span className="inline-flex items-center gap-1">
    <span>{label}</span>
    {required && <span className="text-red-500">*</span>}
  </span>
)

const isFieldRequired = fieldName => REQUIRED_FIELDS.has(fieldName)
const getTrimmedValue = value => (isString(value) ? trim(value) : value)

const EMPTY_DELIVERY_FEE_RULE = { fromKm: 0, toKm: '', price: '' }

const isValueBlank = value => isNil(value) || value === ''

// Ranges are entered as strings by the number inputs; the API wants numbers and
// rejects blank rows.
const toDeliveryFeeRulesPayload = rules =>
  (isArray(rules) ? rules : [])
    .filter(rule => rule && !isValueBlank(rule.toKm) && !isValueBlank(rule.price))
    .map(rule => ({
      fromKm: Number(rule.fromKm) || 0,
      toKm: Number(rule.toKm),
      price: Number(rule.price),
    }))

const baseValidationShape = {
  name: Yup.string().transform(getTrimmedValue).required('Branch name is required'),
  company: Yup.string().required('Company is required'),
  country: Yup.string().transform(getTrimmedValue).required('Country is required'),
  governorate: Yup.string().transform(getTrimmedValue).required('City is required'),
  lat: Yup.number()
    .typeError('Latitude must be a number')
    .min(-90, 'Latitude must be greater than or equal to -90')
    .max(90, 'Latitude must be less than or equal to 90')
    .required('Latitude is required'),
  lon: Yup.number()
    .typeError('Longitude must be a number')
    .min(-180, 'Longitude must be greater than or equal to -180')
    .max(180, 'Longitude must be less than or equal to 180')
    .required('Longitude is required'),
  email: Yup.string()
    .transform(getTrimmedValue)
    .matches(/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/, 'Enter a valid email address')
    .required('Email is required'),
  address: Yup.string().transform(getTrimmedValue).required('Address is required'),
  timeZone: Yup.string().transform(getTrimmedValue).required('TimeZone is not allowed to be empty'),
  phones: Yup.array().of(
    Yup.object({
      title: Yup.string().transform(getTrimmedValue).required('Phone title is required'),
      phone: Yup.string()
        .transform(getTrimmedValue)
        .matches(/^\+?[0-9]+$/, 'Invalid phone number')
        .required('Phone number is required'),
    })
  ),
  whatsappConfirmationAlert: Yup.string()
    .transform(getTrimmedValue)
    .matches(/^\+?[0-9]+$/, 'Invalid WhatsApp number')
    .required('WhatsApp confirmation alert is required'),
  deliveryKmRange: Yup.number()
    .transform(value => (Number.isNaN(value) ? undefined : value))
    .when('isDeliveryAvailable', {
      is: true,
      then: Yup.number().typeError('Delivery range must be a number').moreThan(0, 'Delivery range must be greater than 0').required('Delivery range is required'),
      otherwise: Yup.number().nullable(),
    }),
  // Mirrors the API's coverage rule so the admin sees the problem before saving.
  deliveryFeeRules: Yup.array().when('isDeliveryAvailable', {
    is: true,
    then: Yup.array()
      .of(
        Yup.object({
          toKm: Yup.number().typeError('"To (Km)" must be a number').required('"To (Km)" is required'),
          price: Yup.number().typeError('Price must be a number').min(0, 'Price cannot be negative').required('Price is required'),
        })
      )
      .min(1, 'At least one delivery fee rule is required')
      .test('covers-delivery-range', '', function (rules) {
        const { deliveryKmRange } = this.parent
        const parsed = toDeliveryFeeRulesPayload(rules)

        if (parsed.length !== size(rules)) {
          return this.createError({ message: 'Every delivery fee rule needs both a "To (Km)" and a price.' })
        }

        for (let index = 0; index < parsed.length; index += 1) {
          if (parsed[index].toKm <= parsed[index].fromKm) {
            return this.createError({ message: `Rule ${index + 1}: "To (Km)" must be greater than "From (Km)".` })
          }
        }

        const range = Number(deliveryKmRange)
        if (!range || Number.isNaN(range)) {
          return this.createError({ message: 'Set a delivery range in km before adding delivery fee rules.' })
        }

        // Rules are contiguous, so the last range's end is the furthest distance
        // priced. It must land exactly on the delivery radius.
        const lastToKm = parsed[parsed.length - 1].toKm
        if (lastToKm < range) {
          return this.createError({
            message: `Delivery fee rules must cover the full delivery range: they stop at ${lastToKm} km but the delivery range is ${range} km.`,
          })
        }
        if (lastToKm > range) {
          return this.createError({
            message: `Delivery fee rules cannot go beyond the delivery range: they reach ${lastToKm} km but the delivery range is only ${range} km.`,
          })
        }

        return true
      }),
    otherwise: Yup.array().nullable(),
  }),
}

const getValidationSchema = (isEdit, isCompanyRole, hasAdminAccess) =>
  Yup.object({
    ...baseValidationShape,
    company: !isCompanyRole || hasAdminAccess ? Yup.string().required('Company is required') : Yup.string(),
  })

const BranchProfile = ({ readOnly, loading, refetch, branchData, isEdit, id, roles }) => {
  const { t, i18n } = useTranslation()
  const isArabic = i18n.language === 'ar'

  const timezones = timezonesData.map(tz => ({
    ...tz,
    name: isArabic && tz.nameAr ? tz.nameAr : tz.name,
  }))

  const [previews, setPreviews] = useState({})
  const [files, setFiles] = useState({})
  const [profile, setProfile] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [params, setParams] = useState({ t: 1 })
  const [activeStep, setActiveStep] = useState(1)
  const [companyNames, setCompanyNames] = useState([])
  const [masterCountries, setMasterCountries] = useState([])
  const [masterCities, setMasterCities] = useState([])
  const [dialogOpen, setDialogOpen] = useState(false)
  const [locationData, setLocationData] = useState({
    address: '',
    city: '',
    country: '',
    isLoading: false,
  })
  const formikRef = useRef(null)
  const userFromStore = useSelector(state => get(state, 'user.user'))
  // eslint-disable-next-line
  const isCompanyRole = userFromStore?.roles?.some(role => role.type === 'company')
  // eslint-disable-next-line
  const hasAdminAccess = userFromStore?.roles?.some(role => role.type === 'admin')

  const location = useLocation()

  const navigateToBranches = () => {
    const searchParams = new URLSearchParams(location.search)
    const queryString = searchParams.toString()
    const branchesPath = queryString ? `/branches?${queryString}` : '/branches'
    history.push(branchesPath)
  }

  const [twentyFourSevenTimings, setTwentyFourSevenTimings] = useState(() => {
    const defaultTimings = {
      Sunday: { enabled: true, hours: '24 hrs' },
      Monday: { enabled: true, hours: '24 hrs' },
      Tuesday: { enabled: true, hours: '24 hrs' },
      Wednesday: { enabled: true, hours: '24 hrs' },
      Thursday: { enabled: true, hours: '24 hrs' },
      Friday: { enabled: true, hours: '24 hrs' },
      Saturday: { enabled: true, hours: '24 hrs' },
    }

    const twentyFourSevenMode = find(branchData?.timings, { mode: '24/7' })

    if (twentyFourSevenMode?.timetable) {
      const formattedTimings = { ...defaultTimings }

      forEach(twentyFourSevenMode?.timetable, ({ day, active, time }) => {
        formattedTimings[day] = {
          enabled: active,
          hours: time === '00:00:00' ? '24 hrs' : time,
        }
      })

      return formattedTimings
    }

    return defaultTimings
  })

  const handleTimingChange = (day, field, value) => {
    setTwentyFourSevenTimings(prev => ({
      ...prev,
      [day]: { ...prev[day], [field]: value },
    }))
  }

  const [shiftModeTimings, setShiftModeTimings] = useState(() => {
    const defaultTimings = {
      Sunday: {
        enabled: false,
        shifts: [
          { start: '00:00', end: '00:00' },
          { start: '00:00', end: '00:00' },
        ],
      },
      Monday: {
        enabled: false,
        shifts: [
          { start: '00:00', end: '00:00' },
          { start: '00:00', end: '00:00' },
        ],
      },
      Tuesday: {
        enabled: false,
        shifts: [
          { start: '00:00', end: '00:00' },
          { start: '00:00', end: '00:00' },
        ],
      },
      Wednesday: {
        enabled: false,
        shifts: [
          { start: '00:00', end: '00:00' },
          { start: '00:00', end: '00:00' },
        ],
      },
      Thursday: {
        enabled: false,
        shifts: [
          { start: '00:00', end: '00:00' },
          { start: '00:00', end: '00:00' },
        ],
      },
      Friday: {
        enabled: false,
        shifts: [
          { start: '00:00', end: '00:00' },
          { start: '00:00', end: '00:00' },
        ],
      },
      Saturday: {
        enabled: false,
        shifts: [
          { start: '00:00', end: '00:00' },
          { start: '00:00', end: '00:00' },
        ],
      },
    }

    if (branchData?.timings) {
      const shiftMode = find(branchData?.timings, { mode: 'shift' })

      if (shiftMode?.timetable) {
        const formattedTimings = { ...defaultTimings }

        forEach(shiftMode?.timetable, daySchedule => {
          formattedTimings[daySchedule.day] = {
            enabled: daySchedule.active,
            shifts: [
              {
                start: daySchedule.shift1?.startTime ? daySchedule.shift1.startTime.slice(0, -3) : '00:00',
                end: daySchedule.shift1?.endTime ? daySchedule.shift1.endTime.slice(0, -3) : '00:00',
              },
              {
                start: daySchedule.shift2?.startTime ? daySchedule.shift2.startTime.slice(0, -3) : '00:00',
                end: daySchedule.shift2?.endTime ? daySchedule.shift2.endTime.slice(0, -3) : '00:00',
              },
            ],
          }
        })

        return formattedTimings
      }
    }

    return defaultTimings
  })

  const handleShiftChange = (day, shiftIndex, field, value) => {
    setShiftModeTimings(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        shifts: prev[day].shifts.map((shift, index) => (index === shiftIndex ? { ...shift, [field]: value } : shift)),
      },
    }))
  }

  const handleShiftSwitchChange = (day, checked) => {
    setShiftModeTimings(prev => ({
      ...prev,
      [day]: { ...prev[day], enabled: checked },
    }))
  }

  const [normalModeTimings, setNormalModeTimings] = useState(() => {
    const defaultTimings = {
      Sunday: { enabled: true, startTime: '09:33', endTime: '22:33' },
      Monday: { enabled: true, startTime: '09:33', endTime: '22:33' },
      Tuesday: { enabled: true, startTime: '09:33', endTime: '22:33' },
      Wednesday: { enabled: true, startTime: '09:33', endTime: '22:33' },
      Thursday: { enabled: true, startTime: '09:33', endTime: '22:33' },
      Friday: { enabled: true, startTime: '09:33', endTime: '22:33' },
      Saturday: { enabled: true, startTime: '09:33', endTime: '22:33' },
    }

    if (branchData?.timings) {
      const normalMode = find(branchData?.timings, { mode: 'normal' })

      if (normalMode?.timetable) {
        const formattedTimings = {}

        forEach(normalMode?.timetable, ({ day, active, startTime, endTime }) => {
          const formatTime = timeString => {
            if (!timeString) return '00:00'

            return timeString.slice(0, 5)
          }

          formattedTimings[day] = {
            enabled: active,
            startTime: formatTime(startTime),
            endTime: formatTime(endTime),
          }
        })

        return formattedTimings
      }
    }

    return defaultTimings
  })

  const handleNormalModeChange = (day, field, value) => {
    setNormalModeTimings(prev => ({
      ...prev,
      [day]: { ...prev[day], [field]: value },
    }))
  }

  const history = useHistory()

  useEffect(() => {
    setPreviews({
      profile: branchData?.companyProfile?.profile,
      logoUrl: branchData?.logoUrl,
      cover: branchData?.companyProfile?.cover,
    })
    setProfile({
      username: branchData?.companyProfile?.username,
      ...branchData?.companyProfile?.userKyc,
      twoFactorAuth: branchData?.companyProfile?.twoFactorAuth,
    })
  }, [branchData])

  const setCurrentTab = currentTab => setParams({ t: parseInt(currentTab, 10) })

  const resolveCompanyId = values => values.company || userFromStore?.company?._id || userFromStore?.company || ''

  const addUpdateBranch = async (values, { setSubmitting }) => {
    const companyId = resolveCompanyId(values)
    const data = {
      name: values.name,
      company: companyId,
      country: values.country,
      governorate: values.governorate,
      deliveryKmRange: values.deliveryKmRange,
      deliveryFeeRules: toDeliveryFeeRulesPayload(values.deliveryFeeRules),
      lat: values.lat,
      lon: values.lon,
      email: values.email,
      address: values.address,
      isDeliveryAvailable: values?.isDeliveryAvailable,
      whatsappConfirmationAlert: values.whatsappConfirmationAlert,
      timings: values.timings,
      timeZone: values.timeZone,
    }

    delete data.id
    delete data.logoUrl

    setIsSubmitting(true)
    const formData = new FormData()

    if (files.cover) formData.append('cover', files.cover)
    if (files.profile) formData.append(`profile`, files.profile)
    if (files.logoUrl) formData.append(`logoUrl`, files.logoUrl)
    // eslint-disable-next-line

    if (isArray(values.officeInstructions)) {
      // eslint-disable-next-line
      values.officeInstructions.forEach((instruction, index) => {
        formData.append(`officeInstructions[${index}]`, JSON.stringify(instruction))
      })
    }
    if (isArray(values.phones)) {
      // eslint-disable-next-line
      values.phones.forEach((phone, index) => {
        formData.append(`phones[${index}]`, JSON.stringify(phone))
      })
    }

    // eslint-disable-next-line
    Object.keys(data).forEach(key => {
      if (data[key] === 'null' || data[key] === undefined || data[key] === null) delete data[key]
      else {
        // eslint-disable-next-line
        if (typeof data[key] === 'object' && !Array.isArray(data[key])) {
          formData.append(key, JSON.stringify(data[key]))
        } else {
          formData.append(key, data[key])
        }
      }
    })

    // eslint-disable-next-line
    Object.keys(profile).forEach(key => {
      if (profile[key] === 'null' || profile[key] === undefined || profile[key] === null) delete profile[key]
      // eslint-disable-next-line
      if (typeof profile[key] === 'string' || typeof profile[key] === 'number' || typeof profile[key] === 'boolean' || Array.isArray(profile[key])) {
        formData.append(key, profile[key])
      }
    })

    // The number inputs yield strings; the API validates delivery rules numerically.
    const payload = {
      ...values,
      company: companyId,
      deliveryFeeRules: toDeliveryFeeRulesPayload(values.deliveryFeeRules),
    }
    delete payload.deliveryCost

    try {
      if (isEdit) {
        const response = await apiPut(`branch/${id}`, payload)
        if (response.success) {
          notify.success(t('branchProfile.messages.branchUpdatedSuccessfully'))
          navigateToBranches()
        }
      } else {
        const response = await apiPost('add/branch', payload)
        if (response.success) {
          notify.success(t('branchProfile.messages.branchAddedSuccessfully'))
          navigateToBranches()
        }
      }
    } catch (error) {
      notify.error(t('branchProfile.messages.failedToSaveBranch'))
    } finally {
      setIsSubmitting(false)
      setSubmitting(false)
    }
  }

  useEffect(() => {
    if (hasAdminAccess) {
      fetchCompanies()
    }
  }, [hasAdminAccess])

  useEffect(() => {
    fetchMasterCountries()
  }, [])

  useEffect(() => {
    const countryId = resolveCountryValue(branchData?.country)
    if (countryId) {
      fetchMasterCities(countryId)
    }
  }, [branchData?.country, masterCountries])

  const fetchMasterCountries = async () => {
    try {
      const response = await apiGet('countries', { pageSize: 500, pageNumber: 1 })
      if (response?.data?.countries) {
        setMasterCountries(response.data.countries)
      } else {
        setMasterCountries([])
      }
    } catch (error) {
      console.error('Error fetching countries:', error)
      setMasterCountries([])
    }
  }

  const fetchMasterCities = async countryId => {
    if (!countryId) {
      setMasterCities([])
      return
    }
    try {
      const response = await apiGet('cities', { pageSize: 500, pageNumber: 1, country: countryId })
      if (response?.data?.cities) {
        setMasterCities(response.data.cities)
      } else {
        setMasterCities([])
      }
    } catch (error) {
      console.error('Error fetching cities:', error)
      setMasterCities([])
    }
  }

  const getCountryOptions = () =>
    masterCountries
      .filter(c => c.active !== false)
      .map(c => ({
        id: c._id,
        name: isArabic && c.countryAr ? c.countryAr : c.country,
      }))

  const getGovernorateOptions = currentGovernorate => {
    const options = masterCities
      .filter(c => c.active !== false)
      .map(c => ({
        id: c.city,
        name: isArabic && c.cityAr ? c.cityAr : c.city,
      }))

    if (currentGovernorate) {
      const normalized = toLower(trim(String(currentGovernorate)))
      const hasMatch = some(options, o => toLower(o.id) === normalized)
      if (!hasMatch) {
        options.unshift({ id: currentGovernorate, name: currentGovernorate })
      }
    }

    return options
  }

  const resolveCountryValue = country => {
    if (!country) return ''
    if (isObject(country)) return country._id || ''
    const value = trim(String(country))
    if (/^[0-9a-fA-F]{24}$/.test(value)) return value
    const normalized = toLower(value)
    const match = find(masterCountries, c => toLower(c.country) === normalized)
    return match?._id || ''
  }

  const resolveGovernorateValue = governorate => {
    if (!governorate) return ''
    const normalized = toLower(trim(String(governorate)))
    const match = find(masterCities, c => toLower(c.city) === normalized)
    return match ? match.city : governorate
  }

  const fetchCompanies = async () => {
    const response = await apiGet('companies', { pageSize: 1000 })

    if (response?.data) {
      const companies = response.data.company.map(company => ({
        id: company._id,
        name: company.name,
      }))
      setCompanyNames(companies)
    } else {
      setCompanyNames([])
    }
  }

  const fetchLocationFromCoordinates = async (lat, lon) => {
    if (!lat || !lon) {
      setLocationData({
        address: '',
        city: '',
        country: '',
        isLoading: false,
      })
      return
    }

    setLocationData(prev => ({ ...prev, isLoading: true }))

    try {
      const response = await Geocode.fromLatLng(lat, lon)

      if (response.results && response.results.length > 0) {
        const result = response.results[0]
        const addressComponents = result.address_components

        const address = result.formatted_address || ''
        let city = ''
        let country = ''

        // Extract city and country from address components
        forEach(addressComponents, component => {
          if (includes(component.types, 'locality') || includes(component.types, 'administrative_area_level_1')) {
            city = component.long_name
          }
          if (includes(component.types, 'country')) {
            country = component.long_name
          }
        })

        setLocationData({
          address,
          city,
          country,
          isLoading: false,
        })
      } else {
        setLocationData({
          address: '',
          city: '',
          country: '',
          isLoading: false,
        })
      }
    } catch (error) {
      console.error('Error fetching location data:', error)
      setLocationData({
        address: '',
        city: '',
        country: '',
        isLoading: false,
      })
    }
  }

  const deleteBranch = () => {
    notify.deleteConfirm({
      title: t('branchProfile.messages.deleteBranchTitle'),
      message: t('branchProfile.messages.deleteBranchMessage'),
      onConfirm: async () => {
        const response = await apiDelete(`branch/${id}`)
        if (response?.success) {
          navigateToBranches()
          notify.success(t('branchProfile.messages.branchDeletedSuccessfully'))
        }
      },
    })
  }

  const showPreview = (key, file) => {
    const preview = URL.createObjectURL(file)
    setPreviews({ ...previews, [key]: preview })
  }

  const initialValues = isEdit
    ? {
        name: branchData?.name || '',
        company: branchData?.company || '',
        country: resolveCountryValue(branchData?.country || ''),
        governorate: resolveGovernorateValue(branchData?.governorate || ''),
        deliveryKmRange: branchData?.deliveryKmRange || '',
        deliveryFeeRules: size(branchData?.deliveryFeeRules) > 0 ? branchData.deliveryFeeRules : [EMPTY_DELIVERY_FEE_RULE],
        lat: branchData?.lat || '',
        lon: branchData?.lon || '',
        email: branchData?.email || '',
        address: branchData?.address || '',
        isDeliveryAvailable: branchData?.isDeliveryAvailable || false,
        phones: branchData?.phones || [{ title: '', phone: '' }],
        whatsappConfirmationAlert: branchData?.whatsappConfirmationAlert || '',
        timings: branchData?.timings || [],
        timeZone: branchData?.timeZone,
      }
    : {
        name: '',
        company: isCompanyRole && !hasAdminAccess ? userFromStore?.company?._id || userFromStore?.company || '' : '',
        country: '',
        governorate: '',
        deliveryKmRange: '',
        deliveryFeeRules: [EMPTY_DELIVERY_FEE_RULE],
        lat: '',
        lon: '',
        email: '',
        address: '',
        isDeliveryAvailable: false,
        phones: [{ title: '', phone: '' }],
        whatsappConfirmationAlert: '',
        timings: [],
        timeZone: '',
      }

  const tabs = [
    { id: 1, name: t('branchProfile.tabs.branchDetail'), href: '#', current: false },
    { id: 2, name: t('branchProfile.tabs.branchPhones'), href: '#', current: false },
    { id: 3, name: t('branchProfile.tabs.deliveryDetails'), href: '#', current: false },
    { id: 4, name: t('branchProfile.tabs.branchTimings'), href: '#', current: false },
    { id: 5, name: t('branchProfile.tabs.carStats'), href: '#', current: false },
  ]

  const visibleTabs = isEdit ? tabs : tabs.filter(tab => tab.id !== 4)

  const getSteps = () => {
    return [
      { id: 1, name: t('branchProfile.tabs.branchDetail') },
      { id: 2, name: t('branchProfile.tabs.branchPhones') },
      { id: 3, name: t('branchProfile.tabs.deliveryDetails') },
    ]
  }

  const Stepper = ({ steps, activeStep }) => {
    return (
      <div className="w-full">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => (
            <div key={step.id} className="flex flex-col items-center relative w-full">
              {index < steps.length - 1 && (
                <div className={`absolute top-4 left-[calc(50%+1rem)] w-[calc(100%-2rem)] h-0.5 ${activeStep > step.id ? 'bg-dark-600' : 'bg-gray-300'}`} />
              )}
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center z-10 ${
                  activeStep > step.id ? 'bg-dark-600 text-white' : activeStep === step.id ? 'border-2 border-dark-600 text-dark-600' : 'border-2 border-gray-300 text-gray-400'
                }`}
              >
                {activeStep > step.id ? <CheckIcon className="w-5 h-5" /> : step.id}
              </div>
              <span className={`mt-2 text-sm ${activeStep >= step.id ? 'text-dark-600 font-medium' : 'text-gray-500'}`}>{step.name}</span>
            </div>
          ))}
        </div>
      </div>
    )
  }

  const handleNext = () => {
    if (activeStep < getSteps().length) {
      setActiveStep(activeStep + 1)
    }
  }

  const handlePrevious = () => {
    if (activeStep > 1) {
      setActiveStep(activeStep - 1)
    }
  }

  const formikValidationSchema = getValidationSchema(isEdit, isCompanyRole, hasAdminAccess)

  const [LocationModal, openLocationModal, closeLocationModal] = useModal({
    content: (
      <LocationPicker
        onLocationSelect={location => {
          formikRef.current?.setFieldValue('lat', location.lat)
          formikRef.current?.setFieldValue('lon', location.lon)
          formikRef.current?.setFieldValue('address', location.address)
          closeLocationModal()
        }}
        onClose={() => closeLocationModal()}
        initialLat={formikRef.current?.values?.lat ? parseFloat(formikRef.current.values.lat) : undefined}
        initialLon={formikRef.current?.values?.lon ? parseFloat(formikRef.current.values.lon) : undefined}
      />
    ),
    title: t('branchProfile.modals.selectLocation'),
    dimensions: {
      height: '550px',
      width: '650px',
    },
  })

  return (
    <Formik
      innerRef={ref => {
        formikRef.current = ref
      }}
      initialValues={initialValues}
      onSubmit={addUpdateBranch}
      enableReinitialize
      validationSchema={formikValidationSchema}
      validateOnChange={false}
      validateOnBlur={true}
    >
      {({ values, setFieldValue, isValid, errors, touched, setTouched, validateForm, submitCount, submitForm }) => {
        // Trigger reverse geocoding when lat/lon values change
        useEffect(() => {
          if (values?.lat && values?.lon) {
            fetchLocationFromCoordinates(values.lat, values.lon)
          }
        }, [values?.lat, values?.lon])

        const handleSubmitWithValidationCheck = async e => {
          if (isEdit) {
            e?.preventDefault()
            const validationErrors = await validateForm()

            if (validationErrors && size(validationErrors) > 0) {
              const tab1Fields =
                !isCompanyRole || hasAdminAccess
                  ? ['name', 'company', 'country', 'governorate', 'lat', 'lon', 'email', 'address', 'timeZone']
                  : ['name', 'country', 'governorate', 'lat', 'lon', 'email', 'address', 'timeZone']

              const tab1HasErrors = some(tab1Fields, field => {
                if (field === 'phones') return false
                return get(validationErrors, field)
              })
              const tab2HasErrors =
                get(validationErrors, 'whatsappConfirmationAlert') ||
                (get(validationErrors, 'phones') &&
                  (isArray(get(validationErrors, 'phones'))
                    ? some(get(validationErrors, 'phones'), phoneError => phoneError && (phoneError.title || phoneError.phone))
                    : Boolean(get(validationErrors, 'phones'))))
              const tab3HasErrors = Boolean(get(validationErrors, 'deliveryKmRange') || get(validationErrors, 'deliveryFeeRules'))
              const currentTab = params?.t || 1

              const errorsOnOtherTabs = []
              if (tab1HasErrors && currentTab !== 1) errorsOnOtherTabs.push(1)
              if (tab2HasErrors && currentTab !== 2) errorsOnOtherTabs.push(2)
              if (tab3HasErrors && currentTab !== 3) errorsOnOtherTabs.push(3)

              if (errorsOnOtherTabs.length > 0) {
                const tabNames = {
                  1: t('branchProfile.tabs.branchDetail'),
                  2: t('branchProfile.tabs.branchPhones'),
                  3: t('branchProfile.tabs.deliveryDetails'),
                }
                const tabNamesList = errorsOnOtherTabs.map(tab => tabNames[tab]).join(', ')
                notify.warning('Validation Error', `Required fields are missing. Please check: ${tabNamesList}`)
              }
            }
          }
          submitForm()
        }

        const isValueEmpty = value => isNil(value) || value === ''
        const touchStepOneFields = () => {
          const fieldsToTouch = !isCompanyRole || hasAdminAccess ? STEP_ONE_FIELDS : STEP_ONE_FIELDS.filter(field => field !== 'company')
          const touchedUpdates = fieldsToTouch.reduce((acc, field) => {
            acc[field] = true
            return acc
          }, {})
          setTouched({ ...touched, ...touchedUpdates })
        }

        const touchPhoneFields = () => {
          const touchedPhones = (values?.phones || []).map(() => ({
            title: true,
            phone: true,
          }))
          setTouched({ ...touched, phones: touchedPhones, whatsappConfirmationAlert: true })
        }

        const handleNextClick = async event => {
          event?.preventDefault()
          if (activeStep === 1 || activeStep === 2) {
            const currentErrors = await validateForm()

            if (activeStep === 1) {
              touchStepOneFields()
              const fieldsToCheck = !isCompanyRole || hasAdminAccess ? STEP_ONE_FIELDS : STEP_ONE_FIELDS.filter(field => field !== 'company')
              const hasMissingRequired = some(fieldsToCheck, field => isValueEmpty(get(values, field)))
              const hasSchemaErrors = some(fieldsToCheck, field => get(currentErrors, field))
              if (hasMissingRequired || hasSchemaErrors) {
                return
              }
            }

            if (activeStep === 2) {
              touchPhoneFields()
              const hasPhoneValueGaps = some(values?.phones, phone => isValueEmpty(get(phone, 'title')) || isValueEmpty(get(phone, 'phone')))
              const phoneErrors = get(currentErrors, 'phones')
              const hasPhoneErrors = isArray(phoneErrors) ? some(phoneErrors, errorEntry => errorEntry && (errorEntry.title || errorEntry.phone)) : Boolean(phoneErrors)
              const hasWhatsappAlertError = isValueEmpty(get(values, 'whatsappConfirmationAlert')) || Boolean(get(currentErrors, 'whatsappConfirmationAlert'))

              if (hasPhoneValueGaps || hasPhoneErrors || hasWhatsappAlertError) {
                return
              }
            }
          }

          handleNext()
        }

        return !loading ? (
          <div className="w-full mb-[60px] pb-12 lg:pb-0 flex flex-col">
            <div className="bg-white flex w-full">
              <Form className="space-y-8 py-8 w-full mx-auto px-8">
                <div>
                  <div className="border-b border-gray-200 pb-5 sm:pb-0">
                    <h3 className="text-xl font-medium leading-6 text-gray-900">
                      {t('branchProfile.title')} {readOnly ? t('branchProfile.readOnly') : ''}
                    </h3>
                    <div className="mt-3 sm:mt-4">
                      {!isEdit ? (
                        /* Stepper for add mode */
                        <div className="mb-6">
                          <Stepper steps={getSteps()} activeStep={activeStep} />
                        </div>
                      ) : (
                        /* Tabs for edit mode */
                        <div className="block">
                          <nav className="-mb-px flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-8">
                            {visibleTabs.map(tab => (
                              <span
                                key={tab.name}
                                onClick={() => setCurrentTab(tab.id)}
                                className={classNames(
                                  tab.id === params?.t
                                    ? 'border-l-indigo-500 sm:border-l-transparent sm:border-b-indigo-500 text-indigo-600'
                                    : 'border-l-transparent sm:border-b-transparent text-gray-500 hover:text-gray-700 hover:border-l-gray-300 sm:hover:border-b-gray-300',
                                  'whitespace-nowrap py-2 px-3 sm:py-4 sm:px-0 border-l-4 sm:border-l-0 sm:border-b-2 font-medium text-sm cursor-pointer rounded-r-md sm:rounded-none'
                                )}
                                aria-current={params?.t === tab.id ? 'page' : undefined}
                              >
                                {tab.name}
                              </span>
                            ))}
                          </nav>
                        </div>
                      )}
                    </div>
                  </div>

                  {((!isEdit && activeStep === 1) || (isEdit && params?.t === 1)) && (
                    <div className="space-y-6 sm:space-y-5 pt-8">
                      <div className="grid grid-cols-1 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-gray-200 sm:pt-5 mb-6">
                        <label htmlFor="name" className="block text-sm text-normal text-gray-700 sm:mt-px sm:pt-2">
                          <LabelContent label={t('branchProfile.form.branchName')} required={isFieldRequired('name')} />
                        </label>
                        <div className="mt-1 flex sm:mt-0 w-full max-w-lg sm:col-span-2">
                          <Field
                            type="text"
                            name="name"
                            as={Input}
                            placeholder={t('branchProfile.placeholders.branchName')}
                            extraClassName="w-full"
                            id="name"
                            autoComplete="given-name"
                            className="max-w-lg block w-full shadow-sm focus:ring-dark-500 focus:border-dark-500 sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>
                      </div>

                      <div>
                        {(!isCompanyRole || hasAdminAccess) && (
                          <div className="grid grid-cols-1 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start border-t border-gray-200 sm:pt-5 mb-6">
                            <label htmlFor="branch" className="block text-sm text-normal text-gray-700 sm:mt-px sm:pt-2">
                              <LabelContent label={t('branchProfile.form.company')} required={isFieldRequired('company')} />
                            </label>
                            <div className="mt-1 sm:mt-0 w-full max-w-lg sm:col-span-2">
                              <Selection
                                onChange={value => {
                                  if (value?.id) {
                                    setFieldValue('company', value.id)
                                  }
                                }}
                                title={t('branchProfile.placeholders.selectCompanies')}
                                list={companyNames}
                                value={values?.company || ''}
                                className="w-full"
                              />
                              {touched?.company && errors?.company && <p className="mt-2 text-sm text-red-600">{errors.company}</p>}
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="grid grid-cols-1 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start border-t border-gray-200 sm:pt-5 mb-6">
                        <label htmlFor="email" className="block text-sm text-normal text-gray-700 sm:mt-px sm:pt-2">
                          <LabelContent label={t('branchProfile.form.email')} required={isFieldRequired('email')} />
                        </label>
                        <div className="mt-1 flex sm:mt-0 w-full max-w-lg sm:col-span-2">
                          <Field
                            type="text"
                            name="email"
                            as={Input}
                            placeholder={t('branchProfile.placeholders.enterEmail')}
                            extraClassName="w-full"
                            id="email"
                            className="block shadow-sm focus:ring-dark-500 focus:border-dark-500 sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>
                      </div>
                      {/* <div className="grid grid-cols-1 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start border-t border-gray-200 sm:pt-5 mb-6">
                        <label htmlFor="kmCost" className="block text-sm text-normal text-gray-700 sm:mt-px sm:pt-2">
                          {t('branchProfile.form.kmCost')}
                        </label>
                        <div className="mt-1 flex sm:mt-0 w-full max-w-lg sm:col-span-2">
                          <Field
                            type="text"
                            name="kmCost"
                            as={Input}
                            placeholder={t('branchProfile.placeholders.enterKmCost')}
                            extraClassName="w-full"
                            id="kmCost"
                            className="block shadow-sm focus:ring-dark-500 focus:border-dark-500 sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>
                      </div> */}

                      <div className="grid grid-cols-1 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-gray-200 sm:pt-5 mb-6">
                        <label htmlFor="country" className="block text-sm text-normal text-gray-700 sm:mt-px sm:pt-2">
                          <LabelContent label={t('branchProfile.form.country')} required={isFieldRequired('country')} />
                        </label>
                        <div className="mt-1 sm:mt-0 w-full max-w-lg sm:col-span-2">
                          <Selection
                            onChange={value => {
                              const nextCountry = value?.id || ''
                              setFieldValue('country', nextCountry)
                              setFieldValue('governorate', '')
                              fetchMasterCities(nextCountry)
                            }}
                            title={t('branchProfile.placeholders.selectCountry')}
                            list={getCountryOptions()}
                            value={values?.country || ''}
                            className="w-full"
                            disabled={readOnly}
                          />
                          {touched?.country && errors?.country && <p className="mt-2 text-sm text-red-600">{errors.country}</p>}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-gray-200 sm:pt-5 mb-6">
                        <label htmlFor="governorate" className="block text-sm text-normal text-gray-700 sm:mt-px sm:pt-2">
                          <LabelContent label={t('branchProfile.form.governorate')} required={isFieldRequired('governorate')} />
                        </label>
                        <div className="mt-1 sm:mt-0 w-full max-w-lg sm:col-span-2">
                          <Selection
                            onChange={value => {
                              if (value?.id) {
                                setFieldValue('governorate', value.id)
                              }
                            }}
                            title={t('branchProfile.placeholders.selectGovernorate')}
                            list={getGovernorateOptions(values?.governorate)}
                            value={values?.governorate || ''}
                            className="w-full"
                            disabled={readOnly || !values?.country}
                          />
                          {touched?.governorate && errors?.governorate && <p className="mt-2 text-sm text-red-600">{errors.governorate}</p>}
                        </div>
                      </div>

                      {!readOnly && (
                        <div className="grid grid-cols-1 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start border-t border-gray-200 sm:pt-5 mb-6">
                          <label htmlFor="location" className="block text-sm text-normal text-gray-700 sm:mt-px sm:pt-2">
                            <LabelContent label={t('branchProfile.form.location')} required={false} />
                          </label>
                          <div className="mt-1 flex sm:mt-0 w-full max-w-lg sm:col-span-2">
                            <button
                              type="button"
                              onClick={openLocationModal}
                              className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-dark-600 hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500"
                            >
                              {t('branchProfile.buttons.selectLocation')}
                            </button>
                          </div>
                        </div>
                      )}

                      <div className="grid grid-cols-1 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start border-t border-gray-200 sm:pt-5 mb-6">
                        <label htmlFor="lon" className="block text-sm text-normal text-gray-700 sm:mt-px sm:pt-2">
                          <LabelContent label={t('branchProfile.form.longitude')} required={isFieldRequired('lon')} />
                        </label>
                        <div className="mt-1 flex sm:mt-0 w-full max-w-lg sm:col-span-2">
                          <Field
                            type="number"
                            name="lon"
                            as={Input}
                            placeholder={t('branchProfile.placeholders.enterLongitude')}
                            extraClassName="w-full"
                            id="lon"
                            className="block shadow-sm focus:ring-dark-500 focus:border-dark-500 sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start border-t border-gray-200 sm:pt-5 mb-6">
                        <label htmlFor="lat" className="block text-sm text-normal text-gray-700 sm:mt-px sm:pt-2">
                          <LabelContent label={t('branchProfile.form.latitude')} required={isFieldRequired('lat')} />
                        </label>
                        <div className="mt-1 flex sm:mt-0 w-full max-w-lg sm:col-span-2">
                          <Field
                            type="number"
                            name="lat"
                            as={Input}
                            placeholder={t('branchProfile.placeholders.enterLatitude')}
                            extraClassName="w-full"
                            id="lat"
                            className="block shadow-sm focus:ring-dark-500 focus:border-dark-500 sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>
                      </div>

                      {/* Location Data Display */}
                      {values.lat && values.lon && (
                        <div className="grid grid-cols-1 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start border-t border-gray-200 sm:pt-5 mb-6">
                          <span className="block text-sm text-normal text-gray-700 sm:mt-px sm:pt-2">{t('branchProfile.locationDetails.title')}</span>
                          <div className="mt-1 flex sm:mt-0 w-full max-w-lg sm:col-span-2">
                            <div className="w-full space-y-2">
                              {locationData.isLoading ? (
                                <div className="flex items-center space-x-2">
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-dark-600"></div>
                                  <span className="text-sm text-gray-500">{t('branchProfile.locationDetails.loadingLocationData')}</span>
                                </div>
                              ) : (
                                <>
                                  {locationData.address && (
                                    <div className="p-3 bg-gray-50 rounded-md">
                                      <div className="text-sm font-medium text-gray-700 mb-1">{t('branchProfile.locationDetails.fullAddress')}</div>
                                      <div className="text-sm text-gray-600">{locationData.address}</div>
                                    </div>
                                  )}
                                  {(locationData.city || locationData.country) && (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                      {locationData.city && (
                                        <div className="p-3 bg-gray-50 rounded-md">
                                          <div className="text-sm font-medium text-gray-700 mb-1">{t('branchProfile.locationDetails.city')}</div>
                                          <div className="text-sm text-gray-600">{locationData.city}</div>
                                        </div>
                                      )}
                                      {locationData.country && (
                                        <div className="p-3 bg-gray-50 rounded-md">
                                          <div className="text-sm font-medium text-gray-700 mb-1">{t('branchProfile.locationDetails.country')}</div>
                                          <div className="text-sm text-gray-600">{locationData.country}</div>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  {!locationData.address && !locationData.city && !locationData.country && (
                                    <div className="text-sm text-gray-500 italic">{t('branchProfile.locationDetails.noLocationData')}</div>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="grid grid-cols-1 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start border-t border-gray-200 sm:pt-5 mb-6">
                        <label htmlFor="address" className="block text-sm text-normal text-gray-700 sm:mt-px sm:pt-2">
                          <LabelContent label={t('branchProfile.form.address')} required={isFieldRequired('address')} />
                        </label>
                        <div className="mt-1 flex sm:mt-0 w-full max-w-lg sm:col-span-2">
                          <Field
                            type="text"
                            name="address"
                            as={Input}
                            placeholder={t('branchProfile.placeholders.enterBranchAddress')}
                            extraClassName="w-full"
                            id="address"
                            className="block shadow-sm focus:ring-dark-500 focus:border-dark-500 sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start border-t border-gray-200 sm:pt-5 mb-6">
                        <label htmlFor="address" className="block text-sm text-normal text-gray-700 sm:mt-px sm:pt-2">
                          <LabelContent label={t('branchProfile.form.timeZone')} required={isFieldRequired('timeZone')} />
                        </label>
                        <div className="mt-1 sm:mt-0 w-full max-w-lg sm:col-span-2">
                          <Selection
                            onChange={value => {
                              if (value?.id) {
                                setFieldValue('timeZone', value.id)
                              }
                            }}
                            title={t('branchProfile.placeholders.selectTimeZone')}
                            list={timezones}
                            value={values?.timeZone || ''}
                            className="w-full"
                          />
                          {touched?.timeZone && errors?.timeZone && <p className="mt-2 text-sm text-red-600">{errors.timeZone}</p>}
                        </div>
                      </div>
                    </div>
                  )}

                  {((!isEdit && activeStep === 2) || (isEdit && params?.t === 2)) && (
                    <div className="pt-8 space-y-6">
                      {/* WhatsApp confirmation alert */}
                      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                        <div className="flex items-start gap-4">
                          <span className="flex h-11 w-11 flex-none items-center justify-center rounded-full bg-green-50 text-green-600">
                            <ChatAlt2Icon className="h-6 w-6" />
                          </span>
                          <div className="min-w-0 flex-1">
                            <label htmlFor="whatsappConfirmationAlert" className="block text-sm font-medium text-gray-800">
                              <LabelContent label={t('branchProfile.form.whatsappConfirmationAlert')} required={isFieldRequired('whatsappConfirmationAlert')} />
                            </label>
                            <p className="mt-1 text-xs text-gray-500">{t('branchProfile.placeholders.whatsappConfirmationAlert')}</p>
                            <div className="mt-3">
                              <Field name="whatsappConfirmationAlert">
                                {({ field, form }) => {
                                  const showError = Boolean(touched?.whatsappConfirmationAlert && errors?.whatsappConfirmationAlert)
                                  return (
                                    <>
                                      <InputPhone
                                        id="whatsappConfirmationAlert"
                                        value={field.value || undefined}
                                        onChange={value => {
                                          form.setFieldValue('whatsappConfirmationAlert', value || '')
                                          if (touched?.whatsappConfirmationAlert) {
                                            form.validateField('whatsappConfirmationAlert')
                                          }
                                        }}
                                        onBlur={e => {
                                          field.onBlur(e)
                                          form.setFieldTouched('whatsappConfirmationAlert', true, false)
                                          form.validateField('whatsappConfirmationAlert')
                                        }}
                                        defaultCountry="OM"
                                        placeholder={t('branchProfile.placeholders.whatsappConfirmationAlert')}
                                        inputClassName={classNames(
                                          'block shadow-sm w-full focus:ring-dark-500 focus:border-dark-500 sm:text-sm border-gray-300 rounded-md',
                                          showError && 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                                        )}
                                      />
                                      {showError && <p className="mt-2 text-sm text-red-600">{errors.whatsappConfirmationAlert}</p>}
                                    </>
                                  )
                                }}
                              </Field>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Contact phone numbers */}
                      <FieldArray name="phones">
                        {({ push, remove }) => (
                          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                            <div className="mb-4 flex items-center gap-3">
                              <span className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-gray-100 text-dark-600">
                                <PhoneIcon className="h-5 w-5" />
                              </span>
                              <h4 className="text-sm font-semibold text-gray-800">{t('branchProfile.form.contactNumbers')}</h4>
                            </div>

                            <div className="space-y-4">
                              {size(values.phones) > 0 &&
                                values.phones.map((phone, index) => (
                                  <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                                    <div className="mb-3 flex items-center justify-between">
                                      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-dark-600 text-xs font-bold text-white">{index + 1}</span>
                                      <button
                                        type="button"
                                        onClick={() => remove(index)}
                                        className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm font-medium text-red-600 hover:bg-red-50 hover:text-red-700"
                                      >
                                        <TrashIcon className="h-4 w-4" />
                                        {t('branchProfile.buttons.remove')}
                                      </button>
                                    </div>
                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                      <div>
                                        <label htmlFor={`phones.${index}.title`} className="block mb-2 text-sm text-gray-700">
                                          <LabelContent label={`${t('branchProfile.form.phoneTitle', { number: index + 1 })}`} required={isFieldRequired('phones.title')} />
                                        </label>
                                        <Field
                                          type="text"
                                          name={`phones.${index}.title`}
                                          as={Input}
                                          extraClassName="w-full"
                                          id={`phones.${index}.title`}
                                          inputClassName="border-gray-300"
                                        />
                                      </div>
                                      <div>
                                        <label htmlFor={`phones.${index}.phone`} className="block mb-2 text-sm text-gray-700">
                                          <LabelContent label={`${t('branchProfile.form.phoneNumber', { number: index + 1 })}`} required={isFieldRequired('phones.phone')} />
                                        </label>
                                        <Field
                                          type="text"
                                          name={`phones.${index}.phone`}
                                          as={Input}
                                          extraClassName="w-full"
                                          id={`phones.${index}.phone`}
                                          inputClassName="border-gray-300"
                                        />
                                      </div>
                                    </div>
                                  </div>
                                ))}
                            </div>

                            <button
                              type="button"
                              onClick={() => push({ title: '', phone: '' })}
                              className="mt-4 inline-flex items-center gap-2 rounded-lg border border-dashed border-dark-600 px-4 py-2 text-sm font-medium text-dark-600 hover:bg-dark-600 hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500"
                            >
                              <PlusCircleIcon className="h-5 w-5" />
                              {t('branchProfile.buttons.addPhoneNumber')}
                            </button>
                          </div>
                        )}
                      </FieldArray>
                    </div>
                  )}

                  {((!isEdit && activeStep === 3) || (isEdit && params?.t === 3)) && (
                    <div className="space-y-6 sm:space-y-5 pt-8">
                      <div className="grid grid-cols-1 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start border-t border-gray-200 sm:pt-5 mb-6">
                        <label htmlFor="deliveryCost" className="block text-sm text-normal text-gray-700 sm:mt-px sm:pt-2">
                          {t('branchProfile.form.deliveryAvailability')}
                        </label>
                        <div className="mt-1 flex sm:mt-0 w-full max-w-lg sm:col-span-2">
                          <Switch
                            value={values?.isDeliveryAvailable}
                            onChange={e => {
                              setFieldValue('isDeliveryAvailable', e)
                            }}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start border-t border-gray-200 sm:pt-5 mb-6">
                        <label htmlFor="deliveryKmRange" className="block text-sm text-normal text-gray-700 sm:mt-px sm:pt-2">
                          <LabelContent label={t('branchProfile.form.deliveryRangeKm')} required={values?.isDeliveryAvailable} />
                        </label>
                        <div className="mt-1 flex sm:mt-0 w-full max-w-lg sm:col-span-2">
                          <Field
                            type="number"
                            name="deliveryKmRange"
                            as={Input}
                            extraClassName="w-full"
                            id="deliveryKmRange"
                            placeholder={t('branchProfile.form.deliveryRangePlaceholder')}
                            className="block shadow-sm focus:ring-dark-500 focus:border-dark-500 sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>
                      </div>

                      {/* Distance-based delivery pricing. "From" is derived from the previous
                          row so the ranges are always contiguous and cover 0 -> deliveryKmRange. */}
                      <div className="border-t border-gray-200 sm:pt-5">
                        <FieldArray name="deliveryFeeRules">
                          {({ push, remove }) => {
                            const rules = isArray(values.deliveryFeeRules) ? values.deliveryFeeRules : []
                            const lastRule = rules[rules.length - 1]
                            const coverageError = get(errors, 'deliveryFeeRules')

                            return (
                              <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                                <div className="mb-4">
                                  <h4 className="text-sm font-semibold text-gray-800">{t('branchProfile.form.deliveryFeeRules')}</h4>
                                  <p className="mt-1 text-xs text-gray-500">{t('branchProfile.form.deliveryFeeRulesHint', { range: values?.deliveryKmRange || 0 })}</p>
                                </div>

                                <div className="space-y-4">
                                  {rules.map((rule, index) => (
                                    <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                                      <div className="mb-3 flex items-center justify-between">
                                        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-dark-600 text-xs font-bold text-white">{index + 1}</span>
                                        {rules.length > 1 && index === rules.length - 1 && (
                                          <button
                                            type="button"
                                            onClick={() => remove(index)}
                                            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm font-medium text-red-600 hover:bg-red-50 hover:text-red-700"
                                          >
                                            <TrashIcon className="h-4 w-4" />
                                            {t('branchProfile.buttons.remove')}
                                          </button>
                                        )}
                                      </div>
                                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                                        <div>
                                          <label htmlFor={`deliveryFeeRules.${index}.fromKm`} className="block mb-2 text-sm text-gray-700">
                                            {t('branchProfile.form.deliveryRuleFromKm')}
                                          </label>
                                          <Field
                                            type="number"
                                            name={`deliveryFeeRules.${index}.fromKm`}
                                            as={Input}
                                            extraClassName="w-full"
                                            id={`deliveryFeeRules.${index}.fromKm`}
                                            inputClassName="border-gray-300 bg-gray-100"
                                            disabled
                                          />
                                        </div>
                                        <div>
                                          <label htmlFor={`deliveryFeeRules.${index}.toKm`} className="block mb-2 text-sm text-gray-700">
                                            <LabelContent label={t('branchProfile.form.deliveryRuleToKm')} required={values?.isDeliveryAvailable} />
                                          </label>
                                          <Field
                                            type="number"
                                            name={`deliveryFeeRules.${index}.toKm`}
                                            as={Input}
                                            extraClassName="w-full"
                                            id={`deliveryFeeRules.${index}.toKm`}
                                            inputClassName="border-gray-300"
                                            onChange={e => {
                                              const { value } = e.target
                                              setFieldValue(`deliveryFeeRules.${index}.toKm`, value)
                                              // Keep the next range's start pinned to this one's end.
                                              if (index + 1 < rules.length) {
                                                setFieldValue(`deliveryFeeRules.${index + 1}.fromKm`, value)
                                              }
                                            }}
                                          />
                                        </div>
                                        <div>
                                          <label htmlFor={`deliveryFeeRules.${index}.price`} className="block mb-2 text-sm text-gray-700">
                                            <LabelContent label={t('branchProfile.form.deliveryRulePrice')} required={values?.isDeliveryAvailable} />
                                          </label>
                                          <Field
                                            type="number"
                                            name={`deliveryFeeRules.${index}.price`}
                                            as={Input}
                                            extraClassName="w-full"
                                            id={`deliveryFeeRules.${index}.price`}
                                            inputClassName="border-gray-300"
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>

                                {isString(coverageError) && <div className="mt-3 text-sm text-red-500">{coverageError}</div>}

                                <button
                                  type="button"
                                  onClick={() => push({ fromKm: lastRule ? lastRule.toKm : 0, toKm: '', price: '' })}
                                  className="mt-4 inline-flex items-center gap-2 rounded-lg border border-dashed border-dark-600 px-4 py-2 text-sm font-medium text-dark-600 hover:bg-dark-600 hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500"
                                >
                                  <PlusCircleIcon className="h-5 w-5" />
                                  {t('branchProfile.buttons.addDeliveryRule')}
                                </button>
                              </div>
                            )
                          }}
                        </FieldArray>
                      </div>
                    </div>
                  )}

                  {params?.t === 4 && (
                    <div className="space-y-6 sm:space-y-5 pt-8">
                      <div className="grid grid-cols-1 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start border-t border-gray-200 sm:pt-5 mb-6">
                        <span
                          htmlFor="deliveryCost"
                          className="block text-sm text-normal text-gray-700 sm:mt-px sm:pt-2 cursor-pointer"
                          onClick={() => {
                            setDialogOpen('24/7')
                          }}
                        >
                          {t('branchProfile.form.mode24_7')}
                        </span>
                        <div className="mt-1 flex flex-col sm:flex-row sm:items-center sm:mt-0 w-full max-w-lg sm:col-span-2 gap-2">
                          <div
                            className={`px-2 py-1 text-white font-medium rounded-full ${
                              some(values.timings, { mode: '24/7', isActive: true }) ? 'bg-green-300 text-green-700' : 'bg-yellow-300 text-yellow-700'
                            }`}
                          >
                            {some(values.timings, { mode: '24/7', isActive: true }) ? t('branchProfile.form.active') : t('branchProfile.form.notActive')}
                          </div>
                          <span
                            className={`px-2 py-1 text-white font-medium rounded-full rounded-md text-white bg-dark-600 hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500 cursor-pointer`}
                            onClick={() => {
                              setDialogOpen('24/7')
                            }}
                          >
                            {t('branchProfile.form.adjustTimings')}
                          </span>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start border-t border-gray-200 sm:pt-5 mb-6">
                        <span
                          htmlFor="deliveryCost"
                          className="block text-sm text-normal text-gray-700 sm:mt-px sm:pt-2 cursor-pointer"
                          onClick={() => {
                            setDialogOpen('shift')
                          }}
                        >
                          {t('branchProfile.form.shiftMode')}
                        </span>

                        <div className="mt-1 flex flex-col sm:flex-row sm:items-center sm:mt-0 w-full max-w-lg sm:col-span-2 gap-2">
                          <div
                            className={`px-2 py-1 text-white font-medium rounded-full ${
                              some(values.timings, { mode: 'shift', isActive: true }) ? 'bg-green-300 text-green-700' : 'bg-yellow-300 text-yellow-700'
                            }`}
                          >
                            {some(values.timings, { mode: 'shift', isActive: true }) ? t('branchProfile.form.active') : t('branchProfile.form.notActive')}
                          </div>
                          <span
                            className={`px-2 py-1 text-white font-medium rounded-full rounded-md text-white bg-dark-600 hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500 cursor-pointer`}
                            onClick={() => {
                              setDialogOpen('shift')
                            }}
                          >
                            {t('branchProfile.form.adjustTimings')}
                          </span>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start border-t border-gray-200 sm:pt-5 mb-6">
                        <span
                          htmlFor="deliveryCost"
                          className="block text-sm text-normal text-gray-700 sm:mt-px sm:pt-2 cursor-pointer"
                          onClick={() => {
                            setDialogOpen('normal')
                          }}
                        >
                          {t('branchProfile.form.normalMode')}
                        </span>
                        <div className="mt-1 flex flex-col sm:flex-row sm:items-center sm:mt-0 w-full max-w-lg sm:col-span-2 gap-2">
                          <div
                            className={`px-2 py-1 text-white font-medium rounded-full ${
                              some(values.timings, { mode: 'normal', isActive: true }) ? 'bg-green-300 text-green-700' : 'bg-yellow-300 text-yellow-700'
                            }`}
                          >
                            {some(values.timings, { mode: 'normal', isActive: true }) ? t('branchProfile.form.active') : t('branchProfile.form.notActive')}
                          </div>

                          <span
                            className={`px-2 py-1 text-white font-medium rounded-full rounded-md text-white bg-dark-600 hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500 cursor-pointer`}
                            onClick={() => {
                              setDialogOpen('normal')
                            }}
                          >
                            {t('branchProfile.form.adjustTimings')}
                          </span>
                        </div>
                      </div>

                      {/* Dialog components remain the same */}
                      {dialogOpen === '24/7' && (
                        <BranchTimingDialog
                          isOpen={dialogOpen === '24/7'}
                          onClose={() => setDialogOpen()}
                          timings={twentyFourSevenTimings}
                          onTimingChange={handleTimingChange}
                          onSave={timings => {
                            setFieldValue('timings', [...(reject(values?.timings, { mode: '24/7' }) || []), ...(timings || [])])
                            setDialogOpen()
                          }}
                          values={values}
                          setFieldValue={setFieldValue}
                        />
                      )}

                      {dialogOpen === 'shift' && (
                        <ShiftModeDialog
                          isOpen={dialogOpen === 'shift'}
                          onClose={() => setDialogOpen()}
                          timings={shiftModeTimings}
                          onShiftChange={handleShiftChange}
                          onSwitchChange={handleShiftSwitchChange}
                          onSave={timings => {
                            setFieldValue('timings', [...(reject(values?.timings, { mode: 'shift' }) || []), ...(timings || [])])
                            setDialogOpen()
                          }}
                          values={values}
                          setFieldValue={setFieldValue}
                        />
                      )}

                      {dialogOpen === 'normal' && (
                        <NormalModeDialog
                          isOpen={dialogOpen === 'normal'}
                          onClose={() => setDialogOpen(null)}
                          values={values}
                          setFieldValue={setFieldValue}
                          branchData={branchData}
                          timings={normalModeTimings}
                          onTimeChange={handleNormalModeChange}
                          onSave={timings => {
                            setFieldValue('timings', [...(reject(values?.timings, { mode: 'normal' }) || []), ...(timings || [])])
                            setDialogOpen()
                          }}
                        />
                      )}
                    </div>
                  )}
                  {params?.t === 5 && <DashboardCards branchData={branchData} />}
                </div>

                {params.t !== 5 && !readOnly && (
                  <div className="pt-2 pb-8">
                    {!isEdit ? (
                      /* Stepper navigation buttons for add mode */
                      <div className="flex flex-col sm:flex-row justify-between gap-4">
                        {activeStep === getSteps().length ? (
                          <div className="flex flex-col sm:flex-row w-full gap-4">
                            <div className="flex flex-1 justify-start">
                              {activeStep > 1 && (
                                <button
                                  type="button"
                                  onClick={handlePrevious}
                                  className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500"
                                >
                                  {'Previous'}
                                </button>
                              )}
                            </div>
                            <div className="flex flex-1 justify-end">
                              <button
                                type="submit"
                                disabled={isSubmitting}
                                className={`inline-flex justify-center py-2 px-6 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-dark-600 hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500 ${
                                  isSubmitting && 'opacity-50 cursor-not-allowed'
                                }`}
                              >
                                {isSubmitting ? (
                                  <span className="flex items-center">
                                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                      <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                      ></path>
                                    </svg>
                                    <span>{t('branchProfile.buttons.pleaseWait')}</span>
                                  </span>
                                ) : (
                                  <span>{t('branchProfile.buttons.addBranch')}</span>
                                )}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-col sm:flex-row w-full gap-4">
                            <div className="flex flex-1 justify-start">
                              {activeStep > 1 && (
                                <button
                                  type="button"
                                  onClick={handlePrevious}
                                  className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500"
                                >
                                  {'Previous'}
                                </button>
                              )}
                            </div>
                            <div className="flex flex-1 justify-end">
                              {activeStep < getSteps().length && (
                                <button
                                  type="button"
                                  onClick={handleNextClick}
                                  className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-dark-600 hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500"
                                >
                                  {'Next'}
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      /* Original buttons for edit mode */
                      <div className="flex flex-col sm:flex-row justify-between gap-4">
                        <div className="flex flex-1 justify-start">
                          {(!isCompanyRole || hasAdminAccess) && (
                            <button
                              type="button"
                              onClick={deleteBranch}
                              className={`inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm text-normal rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500`}
                            >
                              {t('branchProfile.buttons.deleteBranch')}
                            </button>
                          )}
                        </div>
                        <div className="flex flex-1 justify-end">
                          <button
                            type={isEdit ? 'button' : 'submit'}
                            onClick={isEdit ? handleSubmitWithValidationCheck : undefined}
                            disabled={isSubmitting}
                            className={`inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-dark-600 hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500 ${
                              isSubmitting && 'opacity-50 cursor-not-allowed'
                            }`}
                          >
                            {isSubmitting ? (
                              <span className="flex items-start">
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                  ></path>
                                </svg>
                                <span>{t('branchProfile.buttons.pleaseWait')}</span>
                              </span>
                            ) : (
                              <span>{t('branchProfile.buttons.update')}</span>
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <LocationModal />
              </Form>
            </div>
          </div>
        ) : (
          <div className="w-full h-[500px] flex items-center justify-center">
            <LoadingIcon />
          </div>
        )
      }}
    </Formik>
  )
}

export default BranchProfile
